import { useState, useMemo, useEffect } from "react";
import { Button } from "components/core/basic/Button";
import AmazonAccountsTable from "./AmazonAccountsTable";
import { Flex } from "../../../../styled-system/jsx";
import { AMC_CLIENT, COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP, MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP } from "views/amc/utils";
import { css } from "../../../../styled-system/css";
import { useAppSelector } from "redux/hooks";
import { useFetch } from "hooks/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { Col, Row } from "react-bootstrap";
import CenteredModal from "components/core/basic/CenteredModal";
import FormGroupTile from "components/core/basic/FormGroupTile";
import ToolTips from "utils/toolTips";
import { successToast, errorToast } from "../../../utils/toasts";
import { FaExclamationTriangle } from "react-icons/fa";

const AMCInstanceSection = () => {
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [showAMCInstancesModal, setShowAMCInstancesModal] = useState(false);
  const [selectedAMCProfiles, setSelectedAMCProfiles] = useState([]);
  const [savedAMCProfiles, setSavedAMCProfiles] = useState([]);
  const [savedDspId, setSavedDspId] = useState("");
  const [dspId, setDspId] = useState("");

  const user = useAppSelector((state) => state.user);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const amazonOnboardingStatus = useAppSelector((state) => state.amz_onboard);
  const queryClient = useQueryClient();

  const { data: advertisingAccountsData, isLoading: isAdvertisingAccountsDataLoading } = useFetch(
    ["advertisingAccounts"],
    "/api/advertising_accounts/",
    { marketplace: marketPlace },
    {
      select: (d) => d.data,
      default: [],
      enabled: user.is_staff && amazonOnboardingStatus.amazon_advertising_access === "connected",
    },
  );

  const { data: advertisingProfileIds } = useFetch(
    ["advertisingProfileIds"],
    "/api/advertising_profile_ids/",
    {},
    {
      select: (d) => d.data,
      default: [],
      enabled: user.is_staff && amazonOnboardingStatus.amazon_advertising_access === "connected",
    },
  );

  const { data: existingAMCInstance } = useFetch(
    ["amcInstanceIds"],
    "/amc/instances",
    {},
    {
      select: (d) => {
        return d.data?.[0];
      },
      default: [],
      enabled: user.is_staff && amazonOnboardingStatus.amazon_advertising_access === "connected",
    },
  );

  const { data: latestAMCInstanceCreationRequestData } = useFetch(
    ["amcInstanceCreationRequests"],
    "/amc/onboarding/instances",
    {},
    {
      select: (d) => d.data.results?.sort((a, b) => b.id - a.id)?.[0],
      default: [],
      enabled: user.is_staff,
    },
  );

  const disableAMCInstanceUpdates =
    latestAMCInstanceCreationRequestData &&
    (latestAMCInstanceCreationRequestData?.instance_creation_status !== "COMPLETED" ||
      latestAMCInstanceCreationRequestData?.advertiser_creation_status !== "COMPLETED") &&
    latestAMCInstanceCreationRequestData?.advertiser_creation_status !== "EXCEPTION";

  const showResubmitDialogue =
    latestAMCInstanceCreationRequestData?.advertiser_creation_status === "EXCEPTION" ||
    latestAMCInstanceCreationRequestData?.instance_creation_status === "EXCEPTION";

  const { data: existingAMCInstanceEntityIds } = useFetch(
    ["amcEntityIdsDerived"],
    "/amc/onboarding/accounts",
    { instance_id: existingAMCInstance?.instance_id },
    {
      select: (d) => {
        return d.data?.advertisers;
      },
      default: [],
      enabled: user.is_staff && !!existingAMCInstance?.instance_id,
    },
  );

  const { existingAMCInstanceAdvertiserEntityIds, existingAMCInstanceDspId } = useMemo(() => {
    return {
      existingAMCInstanceAdvertiserEntityIds:
        existingAMCInstanceEntityIds
          ?.filter((entity) => entity.type === "SPONSORED_ADS")
          ?.map((advertiser) => {
            const { id, marketplaceId } = advertiser;
            return { entityId: id, countryCode: MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP[marketplaceId] };
          }) ?? [],
      existingAMCInstanceDspId: existingAMCInstanceEntityIds?.find((entity) => entity.type === "DISPLAY")?.id,
    };
  }, [existingAMCInstanceEntityIds]);

  useEffect(() => {
    if (
      existingAMCInstanceAdvertiserEntityIds?.length &&
      !existingAMCInstanceAdvertiserEntityIds?.every((id) => selectedEntityIds?.includes(id))
    ) {
      setSelectedEntityIds(existingAMCInstanceAdvertiserEntityIds);
    }

    if (existingAMCInstanceDspId && !dspId) {
      setDspId(existingAMCInstanceDspId);
      setSavedDspId(existingAMCInstanceDspId);
    }
  }, [dspId, existingAMCInstanceAdvertiserEntityIds, existingAMCInstanceDspId, selectedEntityIds]);

  useEffect(() => {
    const profilesWithSavedEntityIds = advertisingAccountsData?.adsAccounts
      .filter((account) =>
        account.alternateIds.some(
          (id) =>
            id.entityId &&
            existingAMCInstanceAdvertiserEntityIds?.map((entity) => entity.entityId)?.includes(id.entityId),
        ),
      )
      ?.map((a) => a.adsAccountId);
    setSelectedAMCProfiles(profilesWithSavedEntityIds);
    setSavedAMCProfiles(profilesWithSavedEntityIds);
  }, [advertisingAccountsData, existingAMCInstanceAdvertiserEntityIds]);

  const amcInstanceData = useMemo(() => {
    let amcInstanceData = advertisingAccountsData?.adsAccounts ?? [];

    amcInstanceData = amcInstanceData.map((profile) => ({
      ...profile,
      alternateIds: profile.alternateIds
        ?.filter((id) => "entityId" in id || "profileId" in id)
        ?.map((entity) => {
          const { instanceId, instanceName } = existingAMCInstance?.raw ?? {};

          return (
              existingAMCInstanceAdvertiserEntityIds?.map((entity) => entity.entityId)?.includes(entity.entityId) ||
                existingAMCInstanceAdvertiserEntityIds?.map((entity) => entity.entityId)?.includes(entity.profileId)
            ) ?
              { ...entity, instance_id: instanceId, instance_name: instanceName }
            : entity;
        }),
    }));

    return amcInstanceData;
  }, [advertisingAccountsData?.adsAccounts, existingAMCInstance?.raw, existingAMCInstanceAdvertiserEntityIds]);

  const createAMCInstance = useMutation({
    mutationFn: async (body) => {
      const res = await api.post("/amc/onboarding/instances/", body);
      return res;
    },

    onSuccess: () => {
      successToast({
        message: "Successfully Requested New AMC Instance",
      });
      setShowAMCInstancesModal(false);
      setSelectedEntityIds(existingAMCInstanceAdvertiserEntityIds ?? []);
      queryClient.invalidateQueries({ queryKey: ["amcInstanceCreationRequests"] });
    },
    onError: (error) => {
      errorToast({ message: "Error Requesting New AMC Instance", error });
    },
  });

  const updateAMCInstance = useMutation({
    mutationFn: async ({ creationRequestId, body }) => {
      const res = await api.patch(`/amc/onboarding/instances/${creationRequestId}/`, body);
      return res;
    },

    onSuccess: () => {
      successToast({
        message: "AMC Instance Update Request Successfully Sent",
      });
      setShowAMCInstancesModal(false);
      setSelectedEntityIds(existingAMCInstanceAdvertiserEntityIds ?? []);
      queryClient.invalidateQueries({ queryKey: ["amcInstanceCreationRequests"] });
    },
    onError: (error) => {
      errorToast({ message: "Error Sending AMC Instance Update Request", error });
    },
  });

  return (
    <Row className="text-start mx-0 w-100" style={{ color: "#393939" }}>
      <Col xs={12} className="px-0">
        <Flex justifyContent={"space-between"} alignItems="center">
          <h2 style={{ fontSize: "28px", fontWeight: 600 }}>Amazon Account IDs</h2>
          <Button
            onPress={() => {
              setShowAMCInstancesModal(true);
            }}
          >
            Manage AMC Instances
          </Button>
        </Flex>
        <AmazonAccountsTable
          accountData={advertisingAccountsData?.adsAccounts ?? []}
          columnsToRender={["countryCodes", "adsAccountId", "entityIds", "profileIds"]}
          isLoading={isAdvertisingAccountsDataLoading}
        />
      </Col>
      <Col xs={12} lg={3} className="d-flex justify-content-end align-items-center px-0">
        <CenteredModal
          show={showAMCInstancesModal}
          closeModal={() => {
            setShowAMCInstancesModal(false);
          }}
          title="Manage AMC Instances"
          content={
            <Flex direction={"column"}>
              {disableAMCInstanceUpdates && !showResubmitDialogue ?
                <>
                  <p className="fs-4 mt-3 mb-4 mx-auto w-75 text-center">
                    Changes to your AMC instance are currently pending. No additional updates can be made until they are
                    complete. Please try again soon.
                  </p>
                  <p className="fs-4 mt-3 mb-4 mx-auto w-75 text-center">
                    <b>Current Instance Status: </b> {latestAMCInstanceCreationRequestData.instance_creation_status}
                  </p>
                  <p className="fs-4 mt-3 mb-4 mx-auto w-75 text-center">
                    <b>Current Advertiser Status: </b> {latestAMCInstanceCreationRequestData.advertiser_creation_status}
                  </p>

                  {latestAMCInstanceCreationRequestData?.advertiser_creation_status === "EXCEPTION" && (
                    <p>{latestAMCInstanceCreationRequestData.advertiser_creation_raw?.details} </p>
                  )}

                  <Flex
                    justifyContent={"center"}
                    className={css({
                      padding: "large",
                    })}
                  >
                    <Button
                      onPress={() => {
                        setShowAMCInstancesModal(false);
                        setSelectedEntityIds(existingAMCInstanceAdvertiserEntityIds ?? []);
                      }}
                    >
                      Continue
                    </Button>
                  </Flex>
                </>
              : <>
                  {showResubmitDialogue ?
                    <>
                      <p className={css({ textAlign: "center" })}>
                        <FaExclamationTriangle /> There was an error creating the requested AMC instance. Please try
                        again.
                      </p>
                      {latestAMCInstanceCreationRequestData.advertiser_creation_raw?.details && (
                        <p>latestAMCInstanceCreationRequestData.advertiser_creation_raw?.details</p>
                      )}
                    </>
                  : <p className="fs-4 mt-3 mb-4 mx-auto w-75 text-center">
                      {existingAMCInstance ?
                        "To update the existing AMC instance associated with this account, select the additional advertising profiles or marketplaces you wish to add."
                      : "To create a new AMC instance, start by selecting your desired advertising profiles below. Once selected, you can choose your preferred marketplaces to include in the new instance."
                      }
                    </p>
                  }
                  {!existingAMCInstance && (
                    <Flex justifyContent={"center"} className={css({ padding: "medium" })}>
                      <Button
                        onPress={() => {
                          const profileIds = Object.values(advertisingProfileIds).map((profile) => profile.id);
                          const recommendedEntityIds = [];
                          const recommendedProfiles = [];

                          for (const profileId of profileIds) {
                            for (const account of advertisingAccountsData?.adsAccounts ?? {}) {
                              const profileEntry = account.alternateIds.find((alt) => alt.profileId === profileId);

                              if (profileEntry) {
                                const { countryCode } = profileEntry;

                                const entityEntry = account.alternateIds.find(
                                  (alt) => alt.entityId && alt.countryCode === countryCode,
                                );

                                if (entityEntry) {
                                  if (!recommendedProfiles.includes(account.adsAccountId)) {
                                    recommendedProfiles.push(account.adsAccountId);
                                  }
                                  recommendedEntityIds.push({
                                    entityId: entityEntry.entityId,
                                    countryCode: countryCode,
                                  });
                                }
                              }
                            }
                          }

                          setSelectedAMCProfiles(recommendedProfiles);
                          setSelectedEntityIds(recommendedEntityIds);
                        }}
                      >
                        Select Recommended Entity IDs
                      </Button>
                    </Flex>
                  )}
                  <AmazonAccountsTable
                    accountData={amcInstanceData}
                    columnsToRender={["countryCodes", "instanceName", "instanceId", "entityIds"]}
                    isLoading={isAdvertisingAccountsDataLoading}
                    allowEntityIdSelection={true}
                    defaultPageSize={5}
                    hideBottomPagination={true}
                    selectionKey="adsAccountId"
                    isSelectable={true}
                    setSelected={(updatedProfiles) => {
                      if (updatedProfiles.length > selectedAMCProfiles.length) {
                        const newProfileId = updatedProfiles.find((p) => !selectedAMCProfiles.includes(p));

                        const entityIdsToAdd = advertisingAccountsData?.adsAccounts
                          .find((a) => a.adsAccountId === newProfileId)
                          .alternateIds?.filter((mp) => "entityId" in mp)
                          ?.filter((id) => id);

                        setSelectedEntityIds((prev) => [...prev, ...entityIdsToAdd]);
                      } else {
                        const removedProfileId = selectedAMCProfiles?.find((p) => !updatedProfiles.includes(p));

                        const entityIdsToRemove = advertisingAccountsData?.adsAccounts
                          ?.find((a) => a.adsAccountId === removedProfileId)
                          ?.alternateIds?.filter((mp) => "entityId" in mp)
                          ?.map((id) => id.entityId);

                        const updatedEntityIds = selectedEntityIds?.filter((id) => {
                          return !entityIdsToRemove.includes(id.entityId);
                        });

                        setSelectedEntityIds(updatedEntityIds);
                      }
                      setSelectedAMCProfiles(updatedProfiles);
                    }}
                    selected={selectedAMCProfiles}
                    selectedEntityIds={selectedEntityIds?.map((entity) => entity.entityId)}
                    savedEntityIds={existingAMCInstanceAdvertiserEntityIds}
                    savedAMCProfiles={savedAMCProfiles}
                    onEntityIdChange={(marketplaceEntity, checked) => {
                      if (checked) {
                        const updatedEntityIds = [...selectedEntityIds, marketplaceEntity];
                        setSelectedEntityIds(updatedEntityIds);
                      } else {
                        const updatedEntityIds = selectedEntityIds?.filter(
                          (id) => id.entityId !== marketplaceEntity.entityId,
                        );
                        setSelectedEntityIds(updatedEntityIds);
                      }
                    }}
                  />
                  <Flex
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={"xlarge"}
                    className={css({ paddingBottom: "xlarge" })}
                  >
                    <div className={css({ width: "50%" })}>
                      <FormGroupTile
                        type="formcontrol"
                        stateName={"newOrgName"}
                        formControlType={"text"}
                        handleChildFormElement={(key, value) => {
                          setDspId(value);
                        }}
                        label={
                          <h6
                            className={css({
                              color: "#73706E",
                              fontWeight: "700",
                              textTransform: "none",
                            })}
                          >
                            DSP Entity ID (optional){" "}
                            <ToolTips
                              toolTip={
                                "This 18-digit ID can be found on the Amazon DSP Campaign Manager dashboard, under the Advertisers section. It is visible directly below the advertiser account name in the Advertiser column."
                              }
                              id={"amcInstanceFormDspIdTooltip"}
                              position={"top"}
                            />
                          </h6>
                        }
                        placeholder={"Enter the DSP ID"}
                        defaultValue={dspId}
                        disabled={savedDspId}
                      />
                    </div>
                  </Flex>
                  <Flex justifyContent={"center"} gap="xlarge">
                    <Button
                      variant="secondary"
                      onPress={() => {
                        setShowAMCInstancesModal(false);
                        setSelectedEntityIds(existingAMCInstanceAdvertiserEntityIds ?? []);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onPress={() => {
                        const body = {
                          instance_creation_request: {},
                          advertiser_creation_request: {},
                          amc_client: AMC_CLIENT,
                        };

                        const formattedAdvertisersList = selectedEntityIds?.map((entity) => {
                          return {
                            type: "SPONSORED_ADS",
                            id: entity.entityId,
                            marketplaceId: COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP[entity.countryCode],
                          };
                        });

                        if (dspId) {
                          formattedAdvertisersList.push({
                            type: "DISPLAY",
                            id: dspId,
                            marketplaceId: null,
                          });
                        }
                        // If updating an existing instance, only new advertiser IDs to add to it are required but sending existing one's doesn't hurt
                        body["advertiser_creation_request"]["advertisersToAdd"] = formattedAdvertisersList;

                        if (
                          existingAMCInstance?.id ||
                          latestAMCInstanceCreationRequestData?.id ||
                          showResubmitDialogue
                        ) {
                          updateAMCInstance.mutate({
                            creationRequestId: latestAMCInstanceCreationRequestData.id,
                            body: body,
                          });
                        } else {
                          createAMCInstance.mutate(body);
                        }
                      }}
                      isDisabled={
                        selectedEntityIds?.length <= existingAMCInstanceAdvertiserEntityIds?.length &&
                        dspId === savedDspId
                      }
                    >
                      {existingAMCInstance?.id || showResubmitDialogue ? "Update" : "Create"} Instance
                    </Button>
                  </Flex>
                </>
              }
            </Flex>
          }
        />
      </Col>
    </Row>
  );
};

export default AMCInstanceSection;
